import { useState, useEffect } from "react";
import { Download, Share, MoreVertical, Smartphone, ExternalLink, Monitor, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWA } from "@/hooks/usePWA";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface InstallButtonProps {
  variant?: "default" | "subtle";
  className?: string;
  showInstructions?: boolean;
}

export function InstallButton({ 
  variant = "default", 
  className,
  showInstructions = true 
}: InstallButtonProps) {
  const { isInstalled, isIOS } = usePWA();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  // Detect if it's an Android tablet (screen width > 600px and Android user agent)
  const isAndroidTablet = typeof navigator !== 'undefined' && 
    /Android/i.test(navigator.userAgent) && 
    window.innerWidth >= 600;

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        setIsInstallable(false);
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error("Error prompting install:", error);
    }
  };

  const openFullyKioskPlayStore = () => {
    window.open('https://play.google.com/store/apps/details?id=de.ozerov.fully', '_blank');
  };

  // Don't show if already installed
  if (isInstalled) return null;

  // If browser supports direct installation, show install button
  if (isInstallable) {
    if (variant === "subtle") {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleInstall}
          className={className}
        >
          <Download className="h-4 w-4 mr-2" />
          Instalar App
        </Button>
      );
    }

    return (
      <Button
        onClick={handleInstall}
        className={`gap-2 ${className || ""}`}
      >
        <Download className="h-5 w-5" />
        Instalar App
      </Button>
    );
  }

  // If showInstructions is false and not installable, don't show anything
  if (!showInstructions) return null;

  // Show instructions dialog for iOS or when beforeinstallprompt isn't available
  const renderInstructionsButton = () => {
    if (variant === "subtle") {
      return (
        <Button variant="ghost" size="sm" className={className}>
          <Smartphone className="h-4 w-4 mr-2" />
          Como Instalar
        </Button>
      );
    }

    return (
      <Button variant="outline" className={`gap-2 ${className || ""}`}>
        <Smartphone className="h-5 w-5" />
        Como Instalar
      </Button>
    );
  };

  const renderIOSInstructions = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Para instalar o app no seu iPhone/iPad:
      </p>
      
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            1
          </div>
          <div className="flex-1">
            <p className="font-medium">Toque no botão Compartilhar</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Share className="h-4 w-4" /> no Safari (barra inferior)
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            2
          </div>
          <div className="flex-1">
            <p className="font-medium">Role e toque em</p>
            <p className="text-sm text-muted-foreground">
              ➕ "Adicionar à Tela de Início"
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            3
          </div>
          <div className="flex-1">
            <p className="font-medium">Confirme tocando em</p>
            <p className="text-sm text-muted-foreground">
              "Adicionar" no canto superior direito
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderChromeInstructions = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Para instalar o app via Chrome:
      </p>
      
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            1
          </div>
          <div className="flex-1">
            <p className="font-medium">Toque no menu do navegador</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <MoreVertical className="h-4 w-4" /> (3 pontos no canto superior)
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            2
          </div>
          <div className="flex-1">
            <p className="font-medium">Toque em</p>
            <p className="text-sm text-muted-foreground">
              "Instalar aplicativo" ou "Adicionar à tela inicial"
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            3
          </div>
          <div className="flex-1">
            <p className="font-medium">Confirme a instalação</p>
            <p className="text-sm text-muted-foreground">
              O app aparecerá na sua tela inicial
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFullyKioskInstructions = () => (
    <div className="space-y-4">
      <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
        <p className="text-sm font-medium text-primary">
          ⭐ Recomendado para tablets de autoatendimento
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          O Fully Kiosk Browser oferece modo tela cheia profissional
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Benefícios:</p>
        <div className="grid gap-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Check className="h-4 w-4 text-green-500" />
            Tela cheia sem barras de navegação
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Check className="h-4 w-4 text-green-500" />
            Bloqueia saída para outros apps
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Check className="h-4 w-4 text-green-500" />
            Inicia automaticamente ao ligar
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Check className="h-4 w-4 text-green-500" />
            Reinicia em caso de travamento
          </div>
        </div>
      </div>

      <Button 
        onClick={openFullyKioskPlayStore} 
        className="w-full gap-2"
      >
        <ExternalLink className="h-4 w-4" />
        Baixar na Play Store
      </Button>

      <div className="space-y-3 pt-2 border-t">
        <p className="text-sm font-medium">Após instalar:</p>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>1. Abra o Fully Kiosk Browser</p>
          <p>2. Cole a URL do tablet na configuração inicial</p>
          <p>3. Ative "Kiosk Mode" nas configurações</p>
          <p>4. Configure "Launch on Boot" para iniciar automático</p>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        {renderInstructionsButton()}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Instalar o Aplicativo
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {isIOS ? (
            renderIOSInstructions()
          ) : isAndroidTablet ? (
            // Android Tablet - Show tabs with Chrome and Fully Kiosk options
            <Tabs defaultValue="fullykiosk" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="fullykiosk" className="gap-1">
                  <Monitor className="h-4 w-4" />
                  Fully Kiosk
                </TabsTrigger>
                <TabsTrigger value="chrome" className="gap-1">
                  <MoreVertical className="h-4 w-4" />
                  Chrome
                </TabsTrigger>
              </TabsList>
              <TabsContent value="fullykiosk" className="mt-4">
                {renderFullyKioskInstructions()}
              </TabsContent>
              <TabsContent value="chrome" className="mt-4">
                {renderChromeInstructions()}
              </TabsContent>
            </Tabs>
          ) : (
            // Android Phone or Desktop
            renderChromeInstructions()
          )}
          
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground text-center">
              Após instalar, o app funcionará offline e abrirá em tela cheia
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
